import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { FavouriteButton } from 'widgets/brownlow-tracker/js/common/FavouriteButton';
import { StatsTableRowHeader } from 'widgets/statspro/js/components/common/stats-table/StatsTableRowHeader';
import { getTranslation } from 'common/react/utils/translations';
import {
    leaderboardFilterSelector,
    leaderboardNavSelector
} from 'store/modules/brownlow-tracker/leaderboard/selectors';
import { UPDATE_PLAYER_MODAL } from 'store/modules/brownlow-tracker/player-modal/actions';
import { PlayerPhoto } from 'common/react/components/PlayerPhoto';
import { useTeamsList } from '../utils';

export const PlayerCell = ({
    player,
    onPlayerFavouriteClick,
    favourites,
    seasonPid,
    shouldHidePredicted,
    isBrownlowV2,
    count,
    status,
    ...cellProps
}) => {
    const dispatch = useDispatch();
    const nav = useSelector(leaderboardNavSelector);
    const query = useSelector(leaderboardFilterSelector);

    const compAbbr = PULSE.app.common.match.getCompAbbr(seasonPid);

    const teamsList = useTeamsList(nav.season.id);

    const teams = [
        { id: -1, name: getTranslation('label.stats.allClubs') },
        ...teamsList.sort((a, b) => a.name.localeCompare(b.name))
    ];

    const seasons = useSelector(
        (state) =>
            state.seasons[`competition_${nav.competition.id}`]?.list ?? []
    );

    // Activate the player modal
    const activatePlayerModal = (event) => {
        if (
            event.keyCode &&
            event.keyCode !== PULSE.app.common.CONSTANTS.KEY_CODES.ENTER &&
            event.keyCode !== PULSE.app.common.CONSTANTS.KEY_CODES.SPACE
        ) {
            return;
        }

        dispatch(
            UPDATE_PLAYER_MODAL.request({
                visible: true,
                playerId: player.playerId,
                seasonPid: seasonPid,
                seasonId: nav.season.id,
                player,
                shouldHidePredicted: shouldHidePredicted,
                isPredictor: isBrownlowV2
            })
        );

        PULSE.app.tracking.recordEvent(
            PULSE.app.common.CONSTANTS.TRACKING.EVENTS.MODAL.VIEW,
            {
                eventData: {
                    /* eslint-disable-next-line camelcase */
                    modal_name: getTranslation(
                        'label.brownlow.tracker.leaderboard.full'
                    )
                }
            }
        );

        if (query.query.length) {
            PULSE.app.analytics.playerSelectionTracking(
                query.query,
                '',
                'Brownlow tracker',
                `${player.givenName} ${player.surname}`,
                count,
                composeFiltersString(nav, teams, seasons)
            );
        }
    };

    return (
        <StatsTableRowHeader
            cellProps={cellProps}
            className="stats-table__row-header-cell"
        >
            <div className="stats-table__row-brownlow-player-cell">
                <FavouriteButton
                    player={player}
                    onPlayerFavouriteClick={onPlayerFavouriteClick}
                    favourites={favourites}
                />

                <PlayerPhoto
                    className={
                        'stats-leaders-table-player__headshot u-hide-until-desktop'
                    }
                    photoUrl={player?.photoURL}
                    scale={'0.2'}
                    alt={`${getTranslation('label.stats.headshot.alt')} ${
                        player.givenName ?? ''
                    } ${player.surname ?? ''}`}
                />

                <div>
                    <button
                        onClick={(event) => activatePlayerModal(event)}
                        onKeyDown={(event) => activatePlayerModal(event)}
                        className={`stats-table__row-brownlow-player-name ${
                            !player.eligible
                                ? 'stats-table__row-brownlow-player-name-ineligible'
                                : ''
                        }`}
                    >
                        <p>{player.givenName}</p>
                        <p>{player.surname}</p>
                    </button>

                    {!player.eligible && (
                        <span className="stats-table__row-brownlow-ineligible">
                            {getTranslation(
                                'label.brownlow.predictor.ineligible'
                            )}
                        </span>
                    )}
                </div>

                {status &&
                    status ===
                        PULSE.app.common.CONSTANTS.MATCH_STATUS.CONCLUDED &&
                    player.winner && (
                        <img
                            className="stats-table__player-cell-brownlow-badge"
                            src={`/resources/${
                                window.RESOURCES_VERSION
                            }/i/elements/${
                                compAbbr === 'AFLW'
                                    ? 'bandf-medal-gold.svg'
                                    : 'brownlow-gold.svg'
                            }`}
                            alt={`${getTranslation(
                                'label.stat.brownlowMedal'
                            )}`}
                        />
                    )}
            </div>
        </StatsTableRowHeader>
    );
};

PlayerCell.propTypes = {
    player: PropTypes.object.isRequired,
    onPlayerFavouriteClick: PropTypes.func,
    favourites: PropTypes.array,
    seasonPid: PropTypes.string,
    shouldHidePredicted: PropTypes.bool,
    isBrownlowV2: PropTypes.bool,
    count: PropTypes.number,
    status: PropTypes.string
};

export const composeFiltersString = (nav, teams, seasons) => {
    let season = {
        name: ''
    };
    let club = {
        name: ''
    };
    if (nav?.season) {
        seasons.length && nav.season.id
            ? (season = seasons.find((s) => s.id === nav.season.id))
            : {
                  name: ''
              };
    }
    if (nav?.team) {
        teams.length && nav.team.id
            ? (club = teams.find((s) => s.id === nav.team.id))
            : {
                  name: ''
              };
    }

    return `${season && `season: ${season.name}, `}${
        club && `club: ${club.name}`
    }`;
};
